import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, Badge } from "@/components/Bits";
import { PRESENCE_DOT, fmtDateTime } from "@/lib/format";
import { Plus, Trash2, UserCog, ShieldOff, Activity, Clock, CheckCircle2, Target } from "lucide-react";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

export default function Team() {
  const { user } = useAuth();
  const canManage = user?.role === "admin";
  const canViewActivity = user?.role === "admin" || user?.role === "supervisor";
  const [presence, setPresence] = useState([]);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const tasks = [
      api.get("/dashboard/presence"),
      api.get("/users").catch(() => ({ data: [] })),
    ];
    if (canViewActivity) tasks.push(api.get("/team/activity").catch(() => ({ data: [] })));
    const res = await Promise.all(tasks);
    setPresence(res[0].data);
    setUsers(res[1].data);
    if (canViewActivity) setActivity(res[2].data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="team-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Team</div>
          <h1 className="text-2xl font-bold tracking-tight">Team Presence & Members</h1>
        </div>
        {canManage && (
          <button data-testid="add-user-btn" onClick={() => setShowNew(true)} className="bg-cyan text-bg font-semibold px-4 py-2 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark">
            <Plus className="w-4 h-4" /> Add Member
          </button>
        )}
      </div>

      {/* Presence cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="presence-grid">
        {presence.map((u) => (
          <div key={u.id} className="bg-surface border border-line rounded-sm p-4 hover:border-cyan/30 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-sm bg-surface2 border border-line flex items-center justify-center font-bold text-cyan">
                  {u.name[0]?.toUpperCase()}
                </div>
                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-surface ${PRESENCE_DOT[u.presence]} ${u.presence === "online" ? "pulse-dot" : ""}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{u.name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">{u.role}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[10px]">
              <div className="border border-line rounded-sm p-2">
                <div className="text-slate-500 uppercase">Status</div>
                <div className={`font-semibold mt-0.5 ${u.presence === "online" ? "text-emerald-400" : u.presence === "away" ? "text-amber-400" : "text-slate-500"}`}>
                  {u.presence.toUpperCase()}
                </div>
              </div>
              <div className="border border-line rounded-sm p-2">
                <div className="text-slate-500 uppercase">Open Tx</div>
                <div className="font-mono text-cyan mt-0.5">{u.open_tickets}</div>
              </div>
            </div>
            <div className="text-[10px] text-slate-500 mt-2 font-mono">Last seen: {fmtDateTime(u.last_seen_at)}</div>
          </div>
        ))}
      </div>

      {/* Activity table (supervisor + admin) */}
      {canViewActivity && (
        <Panel title="Team Activity (Live)" testid="panel-activity" action={
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 flex items-center gap-1.5">
            <Activity className="w-3 h-3 text-cyan" /> refresh tiap 15s
          </div>
        }>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="activity-table">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-line">
                <tr>
                  <th className="text-left px-3 py-2">Agent</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2"><Clock className="w-3 h-3 inline mr-1" />Avg Response (30d)</th>
                  <th className="text-right px-3 py-2">Open</th>
                  <th className="text-right px-3 py-2"><CheckCircle2 className="w-3 h-3 inline mr-1" />Handled Today</th>
                  <th className="text-right px-3 py-2">Resolved Today</th>
                  <th className="text-left px-3 py-2"><Target className="w-3 h-3 inline mr-1" />Current Focus</th>
                </tr>
              </thead>
              <tbody>
                {activity.filter((u) => u.role !== "admin").map((u) => {
                  const avg = u.stats?.avg_response_minutes;
                  const avgColor = avg == null ? "text-slate-500" : avg <= 5 ? "text-emerald-400" : avg <= 15 ? "text-amber-400" : "text-rose-400";
                  const cur = u.stats?.current_ticket;
                  return (
                    <tr key={u.id} className="border-b border-line/40 hover:bg-surface2" data-testid={`activity-row-${u.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="relative">
                            <div className="w-8 h-8 rounded-sm bg-surface2 border border-line flex items-center justify-center text-xs font-bold text-cyan">
                              {u.name[0]?.toUpperCase()}
                            </div>
                            <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-surface ${PRESENCE_DOT[u.presence]}`} />
                          </div>
                          <div>
                            <div className="text-sm font-semibold">{u.name}</div>
                            <div className="text-[10px] text-slate-500">{u.role}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] uppercase tracking-wider font-semibold ${u.presence === "online" ? "text-emerald-400" : u.presence === "away" ? "text-amber-400" : "text-slate-500"}`} data-testid={`presence-${u.id}`}>
                          {u.presence}
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 text-right font-mono text-sm ${avgColor}`} data-testid={`avg-response-${u.id}`}>
                        {avg == null ? "—" : `${avg}m`}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-cyan" data-testid={`open-tickets-${u.id}`}>{u.stats?.open_tickets ?? 0}</td>
                      <td className="px-3 py-2.5 text-right font-mono" data-testid={`handled-today-${u.id}`}>{u.stats?.handled_today ?? 0}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-400" data-testid={`resolved-today-${u.id}`}>{u.stats?.resolved_today ?? 0}</td>
                      <td className="px-3 py-2.5" data-testid={`current-ticket-${u.id}`}>
                        {cur ? (
                          <Link to={`/tickets/${cur.id}`} className="flex items-center gap-2 group">
                            <span className="font-mono text-xs text-cyan group-hover:underline">{cur.ticket_number}</span>
                            <Badge color={cur.system === "SATURNUS" ? "info" : "muted"} className="text-[9px]">{cur.system === "SATURNUS" ? "STPAY" : "TARA"}</Badge>
                            <span className="text-xs text-slate-400 truncate max-w-[180px]">{cur.merchant_name || cur.customer_name || "—"}</span>
                            <span className="text-[9px] text-slate-500 uppercase">{(cur.status || "").replace(/_/g, " ")}</span>
                          </Link>
                        ) : (
                          <span className="text-xs text-slate-500">— tidak ada aktivitas</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {activity.filter((u) => u.role !== "admin").length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-xs text-slate-500">Belum ada data aktivitas.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* Members table */}
      {canManage && (
        <Panel title="Members" testid="panel-members">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-line">
              <tr>
                <th className="text-left px-2 py-2">Name</th>
                <th className="text-left px-2 py-2">Email</th>
                <th className="text-left px-2 py-2">Role</th>
                <th className="text-left px-2 py-2">2FA</th>
                <th className="text-left px-2 py-2">Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-line/40 hover:bg-surface2">
                  <td className="px-2 py-2">{u.name}</td>
                  <td className="px-2 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-2 py-2"><Badge color={u.role === "admin" ? "error" : u.role === "supervisor" ? "info" : "muted"}>{u.role}</Badge></td>
                  <td className="px-2 py-2">
                    {u.role === "admin" ? (
                      <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500" data-testid={`2fa-status-${u.id}`}>exempt</span>
                    ) : u.totp_enabled ? (
                      <Badge color="success" data-testid={`2fa-status-${u.id}`}>active</Badge>
                    ) : (
                      <Badge color="warning" data-testid={`2fa-status-${u.id}`}>pending</Badge>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-400">{fmtDateTime(u.created_at)}</td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {u.role !== "admin" && u.totp_enabled && (
                      <button
                        data-testid={`reset-2fa-${u.id}`}
                        onClick={async () => {
                          if (!confirm(`Reset 2FA untuk ${u.email}? User akan diminta setup ulang Authenticator saat login berikutnya.`)) return;
                          try { await api.post(`/users/${u.id}/reset-2fa`); load(); }
                          catch (e) { alert(formatErr(e)); }
                        }}
                        className="text-xs text-amber-400 hover:underline mr-3"
                        title="Reset 2FA"
                      >
                        <ShieldOff className="w-3.5 h-3.5 inline" />
                      </button>
                    )}
                    <button data-testid={`edit-user-${u.id}`} onClick={() => setEditing(u)} className="text-xs text-cyan hover:underline mr-3"><UserCog className="w-3.5 h-3.5 inline" /></button>
                    {u.id !== user.id && (
                      <button data-testid={`delete-user-${u.id}`} onClick={async () => { if (confirm("Delete?")) { await api.delete(`/users/${u.id}`); load(); } }} className="text-xs text-rose-400 hover:underline"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {(showNew || editing) && (
        <UserModal
          u={editing}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function UserModal({ u, onClose, onSaved }) {
  const isNew = !u;
  const [f, setF] = useState(u || { email: "", name: "", role: "agent", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => setRoles([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (isNew) await api.post("/users", f);
      else {
        const body = { name: f.name, role: f.role };
        if (f.password) body.password = f.password;
        await api.patch(`/users/${u.id}`, body);
      }
      onSaved();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="user-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-sm p-5 w-full max-w-md space-y-3">
        <h3 className="text-lg font-bold">{isNew ? "Add Member" : "Edit Member"}</h3>
        {isNew && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Email</label>
            <input data-testid="um-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${ipt} font-mono`} />
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Name</label>
          <input data-testid="um-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={ipt} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Role</label>
          <select data-testid="um-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={ipt}>
            {roles.length === 0 ? (
              <>
                <option value="agent">Agent</option>
                <option value="supervisor">Supervisor</option>
                <option value="admin">Admin</option>
              </>
            ) : (
              roles.map((r) => (
                <option key={r.code} value={r.code}>{r.name}{r.is_built_in ? "" : " (custom)"}</option>
              ))
            )}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Password {isNew ? "" : "(kosongkan jika tidak diubah)"}</label>
          <input data-testid="um-password" type="password" required={isNew} value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} className={`${ipt} font-mono`} />
        </div>
        {err && <div className="text-xs text-rose-400">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-line px-4 py-2 text-sm rounded-sm hover:bg-surface2">Cancel</button>
          <button data-testid="um-save" disabled={busy} className="bg-cyan text-bg font-semibold px-4 py-2 text-sm rounded-sm hover:bg-cyan-dark disabled:opacity-60">Save</button>
        </div>
      </form>
    </div>
  );
}
